import React, { useEffect, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import moment from 'moment';
import { sumBy } from 'lodash';
import { toast } from 'react-toastify';
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Fade,
  IconButton,
  IconButtonProps,
  List,
  ListItem,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAppDispatch } from 'store/hooks';
import { updateResponse, fetchResponses } from 'store/entities/dashboard/dashboardThunk';
import useWindowSize from 'common/hook/useWindowSize';
import { ISize } from 'interfaces/Size';
import { ArchiveButton, Button, Modal, TitleText, ToastMessage, Typo } from 'components/primitives';
import useStyles from './DashboardMain.styles';
import {
  EmptyRequestSnippet,
  RequestResource,
  ResponseCard,
  PreLoaderSnippet,
} from 'components/snippets';
import { DashboardMainModal, DashboardMainProps, DashboardModalFocus } from './DashboardMain.props';
import {
  AcceptResources,
  DeclineResources,
  ProvidedResources,
  RequestItemBox,
  UserAddForm,
} from 'components/fragments';
import { RequestData } from 'interfaces/request.interface';
import { IResponsesDB } from 'interfaces/response.interface';
import { QueryState, ResponseStatus } from 'common/enums';
import { ToastStatus } from 'common/enums/ToastStatus';
import { formatDeclineResources } from 'common/utils';
import { IRequestAmbulance, IRequestPersonnel } from 'interfaces/ResourceRequests';
import { FormProvider } from 'react-hook-form';
import IncidentsManage from 'pages/Dashboard/IncidentsManage';
import {
  AddBox as AddBoxIcon,
  AddCircleRounded as AddCircleRoundedIcon,
  ChevronRight as ChevronRightIcon,
  CloseRounded,
} from '@mui/icons-material';
import CreateIcon from '@mui/icons-material/Create';
import { IncidentsManageProps } from '../IncidentsManage/IncidentsManage.props';
import { syncFilledFromRequests } from 'common/utils/syncFilledFromRequests';
import { PaperLayout } from 'components/layouts';
import { AccountClockIcon, AlarmLightIcon, InsertChartIcon, SquareEditIcon } from 'assets/icons';
import { ConfirmationModal } from 'components/modals';
import { RequestDetailModal } from '../RequestDetails/RequestDetails.props';
import {
  execArchiveRequest,
  remindAgency,
  remindAll,
} from '../../../store/entities/requestdetail/requestdetailThunk';
import { INotifications } from 'interfaces/notification.interface';
import { useNavigate } from 'react-router-dom';
import { updateProcessing } from 'store/entities/dashboard/dashboardSlice';
import { ISelectProp } from 'interfaces/SelectProp';
import { BoxProps } from '@mui/material/Box';
import { ResourceType } from 'interfaces/ResourceType';
import theme from '../../../theming/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { ClassNameMap } from '@mui/styles';
import Grid from '@mui/material/Unstable_Grid2';
import { AmbulanceOptions, JobPositions } from '../../../common/selectoptions';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const selectLabel = (
  value: string | undefined,
  SelectOptions: ISelectProp[],
): string | undefined => {
  if (value) {
    const findValue = SelectOptions.find((m: ISelectProp) => m.value === value);
    return findValue?.label;
  } else {
    return undefined;
  }
};

const emailTemplate =
  'Attention Director and/or point of contact personnel, thank you for your support and offer ' +
  'for the requested EMS Mobilization response. At this time, the response is full. ' +
  'Please continue to monitor for further requests as this request continues to expand. ' +
  'Thank you for your continued support.';
const DashboardMainView: React.FC<DashboardMainProps> = ({
  loading,
  requests,
  isRequestProcessing,
  serviceActionRequest,
  responses,
  modalFocus,
  childModalFocus,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const windowSize: ISize = useWindowSize();
  const classes = useStyles(windowSize);
  const [filterResponses, setFilterResponses] = useState<IResponsesDB[]>([]);
  const [syncFilledResponses, setSyncFilledResponses] = useState<any[]>([]);
  const isRequestsEmpty = Array.isArray(requests) && requests.length === 0;
  const ttlRequestCount = props.pagesTotal;
  const countResponses = filterResponses.length || 0;
  const processingArchive = false; //isProcessing && serviceAction === QueryState.ARCHIVED;
  const sendingNotification =
    isRequestProcessing && serviceActionRequest === QueryState.NOTIFICATION;

  const [emailMsg, setEmailMsg] = useState<string>(emailTemplate);
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalKey, setModalKey] = useState(0);
  const refreshModalByKey = () => setModalKey((k) => k + 1);

  useEffect(() => {
    const newFilterResponses = Array.isArray(responses) ? responses : [];
    setFilterResponses(newFilterResponses);

    const newSyncFilledResponses = syncFilledFromRequests(newFilterResponses, requests || []);
    setSyncFilledResponses(newSyncFilledResponses);
    if (modalFocus.modal === DashboardMainModal.ACCEPT && modalFocus?.request?.id) {
      const pendingResponses = newSyncFilledResponses.filter(
        (response) => response.requests.id === modalFocus?.request?.id,
      );
      modalFocus.responses = pendingResponses;
      modalFocus.request = requests?.find((r) => r.id === modalFocus?.request?.id);
    }
  }, [responses, requests]);

  const onPerformResponse = (responses: IResponsesDB[], request: RequestData) => {
    props.handleModalFocus({
      modal: DashboardMainModal.ACCEPT,
      responses: responses,
      request: request,
      isRequestProcessing: true,
    });
    // props.handleModalFocus({ modal: DashboardMainModal.DECLINE, response: response });
  };

  const onAcceptResources = async (
    payload: IResponsesDB,
    original: IResponsesDB | undefined,
    status: DashboardMainModal,
    modalFocus: DashboardModalFocus,
  ) => {
    try {
      //if (ok) {
      dispatch(updateProcessing({ status: true }));
      const details = payload.requests;
      const orgAmbulance = original?.requests.ambulance || [];
      const resAmbulance = details.ambulance;
      const orgPersonnel = original?.requests.personnel || [];
      const resPersonnel = details.personnel;

      for (let i = 0; i < orgAmbulance?.length; i++) {
        const el = orgAmbulance[i];
        resAmbulance[i].id = el.id;
      }
      for (let i = 0; i < orgPersonnel?.length; i++) {
        const el = orgPersonnel[i];
        resPersonnel[i].id = el.id;
      }

      const responsePayload: IResponsesDB = {
        ...payload,
        requests: {
          ...details,
          ambulance: resAmbulance,
          personnel: resPersonnel,
        },
        status,
      };

      await dispatch(
        updateResponse({ response: responsePayload, status: ResponseStatus.ACCEPTED }),
      ).unwrap();

      await dispatch(fetchResponses());
      dispatch(updateProcessing({ status: false }));

      toast.info(<ToastMessage status={ToastStatus.SUCCESS} message='Successfully Process' />);

      //props.handleModalFocus(modalFocus);
      //refreshModalByKey();
      //props.handleModalFocus({ modal: DashboardMainModal.UNDEFINED });
      //} else {
      //props.handleModalFocus({ modal: DashboardMainModal.UNDEFINED });
      //}
    } catch {
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />);
    }
  };

  const onOpenDeclineOfferModal = (response: IResponsesDB) => {
    props.handleChildModalFocus({
      modal: DashboardMainModal.DECLINE,
      response: response,
    });
  };

  const onDeclineResources = async (
    ok: boolean,
    payload: IResponsesDB,
    status: DashboardMainModal,
  ) => {
    try {
      if (ok) {
        const ambulance = formatDeclineResources<IRequestAmbulance>(payload.requests.ambulance);
        const personnel = formatDeclineResources<IRequestPersonnel>(payload.requests.personnel);
        const responsePayload: IResponsesDB = {
          ...payload,
          requests: {
            ...payload.requests,
            ambulance,
            personnel,
          },
          status,
          declineMessage: emailMsg,
        };

        await dispatch(
          updateResponse({ response: responsePayload, status: ResponseStatus.REJECTED }),
        ).unwrap();

        await dispatch(fetchResponses());

        toast.info(<ToastMessage status={ToastStatus.SUCCESS} message='Successfully Process' />);
        props.handleChildModalFocus({ modal: DashboardMainModal.UNDEFINED });
      } else {
        props.handleChildModalFocus({ modal: DashboardMainModal.UNDEFINED });
      }
    } catch {
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />);
    }
  };

  const onOpenArchiveModal = () => {
    //if (!isArchived) {
    props.handleModalFocus({ modal: DashboardMainModal.ARCHIVED });
    //}
  };

  const onConfirmArchive = async (confirm: boolean, request?: RequestData) => {
    try {
      if (confirm) {
        //if (!isProcessing && !isArchived && request?.id) {
        if (request?.id) {
          await dispatch(execArchiveRequest({ id: request.id })).unwrap();
          toast.info(<ToastMessage status={ToastStatus.SUCCESS} message='Successfully Process' />);
          props.resetModalFocus();
          navigate('/dashboard');
        } else {
          // request missing — just reset modal focus (no archive action)
          props.resetModalFocus();
        }
      } else {
        props.resetModalFocus();
      }
    } catch (error) {
      console.log(error);
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />);
    }
  };

  const onOpenSendRemindModal = (request: RequestData) => {
    //setRemindStationName(agencyName);
    props.handleModalFocus({
      modal: DashboardMainModal.REMIND,
      request: request,
    });
  };

  const onOpenOffersModal = (request: RequestData) => {
    if (request) {
      props.handleModalFocus({
        modal: DashboardMainModal.OFFERS,
        request: request,
        responses: request.responses,
      });
    }
  };

  const onConfirmSendReminder = async (
    accept: boolean,
    isRemindAll?: boolean,
    payload?: { requestId: string; agencyId: string },
  ) => {
    try {
      if (accept) {
        if (isRemindAll && payload && !sendingNotification) {
          await dispatch(remindAll(payload.requestId));
        } else {
          if (payload && !sendingNotification) {
            await dispatch(remindAgency(payload)).unwrap();
          }
        }
        toast.info(<ToastMessage status={ToastStatus.SUCCESS} message='Notification Sent' />);
        props.resetModalFocus();
      } else {
        props.resetModalFocus();
      }
    } catch (error) {
      console.log(error);
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />);
    }
  };

  const onOpenProvidedModal = (request: RequestData) => {
    if (request) {
      props.handleModalFocus({
        modal: DashboardMainModal.PROVIDED_RESOURCES,
        request: request,
        responses: request.responses,
      });
    }
  };

  const onCloseOffersModal = () => {
    props.resetModalFocus();
  };

  const onCloseProvideResources = () => {
    props.resetModalFocus();
  };

  const calculateTotals = (request: RequestData) => {
    const ambulance = request.ambulance || [];
    const personnel = request.personnel || [];
    const reqPersonnel = sumBy(personnel, (value) => Number(value.number) || 0);
    const reqAmbulance = sumBy(ambulance, (value) => Number(value.number) || 0);
    const acceptedPersonnel = personnel
      .flatMap((p) => {
        const pa = [];
        pa.push(p.items?.filter((i) => i.accepted));
        return pa;
      })
      .flat();
    const acceptedPersonnelCount = (acceptedPersonnel || []).reduce(
      (sum: number, item) => sum + (item && item.number ? item.number : 0),
      0,
    );
    const acceptedAmbulance = ambulance
      .flatMap((a) => {
        const am = [];
        am.push(a.items?.filter((i) => i.accepted));
        return am;
      })
      .flat();
    const acceptedAmbulanceCount = (acceptedAmbulance || []).reduce(
      (sum: number, item) => sum + (item && item.number ? item.number : 0),
      0,
    );
    return [reqPersonnel, acceptedPersonnelCount, reqAmbulance, acceptedAmbulanceCount];
  };

  return (
    <>
      <Container component='main'>
        <TitleText style={{ display: 'flex' }} title='Requests'>
          {!isRequestsEmpty && (
            <Box className={classes.responseBadge}>
              <Typo variant='h4'>{ttlRequestCount}</Typo>
            </Box>
          )}
          <Button
            alternate
            startIcon={<CreateIcon fontSize='large' />}
            onClick={() => props.handleManageIncidentModal(true)}>
            Manage incidents
          </Button>
        </TitleText>

        <Fade in={isRequestsEmpty && !loading}>
          <Box>
            {isRequestsEmpty && (
              <EmptyRequestSnippet onCreate={() => props.navigateTo('request/new')} />
            )}
          </Box>
        </Fade>

        <Fade in={!isRequestsEmpty}>
          <Box>
            <Stack spacing={1}>
              {requests &&
                requests.map((i, idx) => {
                  const createdAt = moment(i.createdAt).format('YYYY / MM / DD');
                  const counties = i.counties.length || 0;
                  const agenciesCount = (i.agencies && i.agencies.length) || 0;
                  const request = i;
                  const isArchived = request?.archived === 1;
                  const offerCount = (i.responses || []).filter((r) => r.status === 0).length;
                  const ambulance = i.ambulance || [];
                  const personnel = i.personnel || [];
                  const reqPersonnel = sumBy(personnel, (value) => Number(value.number) || 0);
                  const reqAmbulance = sumBy(ambulance, (value) => Number(value.number) || 0);
                  const acceptedPersonnel = personnel
                    .flatMap((p) => {
                      const pa = [];
                      pa.push(p.items?.filter((i) => i.accepted));
                      return pa;
                    })
                    .flat();
                  const acceptedPersonnelCount = (acceptedPersonnel || []).reduce(
                    (sum: number, item) => sum + (item && item.number ? item.number : 0),
                    0,
                  );
                  const acceptedAmbulance = ambulance
                    .flatMap((a) => {
                      const am = [];
                      am.push(a.items?.filter((i) => i.accepted));
                      return am;
                    })
                    .flat();
                  const acceptedAmbulanceCount = (acceptedAmbulance || []).reduce(
                    (sum: number, item) => sum + (item && item.number ? item.number : 0),
                    0,
                  );
                  const numResp = i.responses?.length || 0;
                  const pendingResponses = syncFilledResponses.filter(
                    (response) => response.requests.id === request?.id,
                  );
                  return (
                    <>
                      <RequestItemBox
                        className={classes.root}
                        sx={{ cursor: 'pointer' }}
                        key={i.id || idx}
                        name={`Request #${i.requestNumber} ${i.incident?.name || ''}`}
                        meta={[
                          { label: 'Created on:', value: createdAt },
                          {
                            label: 'Sent to:',
                            value: `${agenciesCount} agencies`,
                          },
                          { label: 'Responses:', value: JSON.stringify(numResp) },
                        ]}>
                        <RequestResource
                          title='Personnel'
                          meta={[
                            {
                              label: 'Requested',
                              value: reqPersonnel,
                            },
                            { label: 'Accepted', value: acceptedPersonnelCount },
                          ]}
                        />
                        <RequestResource
                          title='Ambulance'
                          meta={[
                            { label: 'Requested', value: reqAmbulance },
                            { label: 'Accepted', value: acceptedAmbulanceCount },
                          ]}
                        />
                        <IconButton
                          className={classes.more}
                          onClick={() => onOpenProvidedModal(request)}>
                          <ChevronRightIcon fontSize='small' />
                          <Typo textAlign='center' color='textSecondary' variant='body2'>
                            <small>See details</small>
                          </Typo>
                        </IconButton>
                      </RequestItemBox>
                      <PaperLayout
                        elevation={1}
                        sx={{
                          boxShadow: 'none',
                          paddingRight: '16px',
                          paddingLeft: '8px',
                          paddingBottom: '12px',
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                          marginTop: '0 !important',
                        }}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between'>
                          <Stack
                            spacing={1}
                            direction='row'
                            alignItems='center'
                            classes={classes.root}>
                              <Button
                                sx={{ minWidth: '140px', borderWidth: 0 }}
                                startIcon={
                                  <Box className={classes.iconContainer}>
                                    <SquareEditIcon fontSize='medium' />
                                  </Box>
                                }
                                onClick={() => props.onEditRequest(request)}
                                alternate>
                                Edit request
                              </Button>
                            <Button
                              sx={{ borderWidth: 0 }}
                              startIcon={
                                !isMobile && (
                                  <Box className={classes.iconContainer}>
                                    <AccountClockIcon fontSize={'medium'} />
                                  </Box>
                                )
                              }
                              onClick={() => props.onChangeShift(request)}
                              alternate>
                              {isMobile ? <AccountClockIcon fontSize={'large'} /> : 'Shift change'}
                            </Button>
                            <Button
                              sx={{ borderWidth: 0 }}
                              startIcon={
                                !isMobile && (
                                  <Box className={classes.iconContainer}>
                                    <AlarmLightIcon fontSize={'medium'} />
                                  </Box>
                                )
                              }
                              fullWidth={isCompact}
                              onClick={() => onOpenSendRemindModal(request)}
                              alternate>
                              {isMobile ? <AlarmLightIcon fontSize={'large'} /> : 'Remind'}
                            </Button>
                          </Stack>
                          <Button
                            disabled={offerCount < 1}
                            fullWidth={false}
                            onClick={() => onPerformResponse(pendingResponses, request)}>
                            {offerCount} New Offers
                          </Button>
                        </Stack>
                      </PaperLayout>
                    </>
                  );
                })}
            </Stack>
          </Box>
        </Fade>
        {props.lastPage > 1 && (
          <Pagination
            className={classes.pagination}
            count={props.lastPage}
            page={props.page}
            shape='rounded'
            color='primary'
            onChange={(e, num) => props.handlePageChange(num)}
          />
        )}
      </Container>

      <Modal style={{ padding: 0, borderRadius: '8px' }} open={props.manageIncidentModal}>
        <div style={{ backgroundColor: '#e5e5e5', borderRadius: '8px' }}>
          <IncidentsManage {...(props as DashboardMainProps)} />
        </div>
      </Modal>

      <Modal
        style={{ backgroundColor: '#ECEEF2' }}
        open={modalFocus.modal === DashboardMainModal.ACCEPT}>
        <Box className={classes.offersModalContent}>
          <Box className={classes.closeIconContainer}>
            <CloseRounded onClick={onCloseOffersModal} />
          </Box>
          <Box className={classes.modalTitle}>
            <Typo className={classes.modalTitleText}>Resource offers</Typo>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 40px)' }}>
            <RequestTotals
              isMobile={isMobile}
              classes={classes}
              modalFocus={modalFocus as DashboardModalFocus}
            />
            <Box className={classes.acceptResourcesContent}>
              {modalFocus?.responses?.length
                ? modalFocus?.responses.map((response, idx) => {
                    return (
                      <AcceptResources
                        key={idx}
                        requestResponse={response}
                        onAccept={(x: boolean, payload) => {
                          onAcceptResources(
                            payload,
                            response,
                            DashboardMainModal.ACCEPT,
                            modalFocus as DashboardModalFocus,
                          );
                        }}
                        onDecline={(x: boolean, payload) => onOpenDeclineOfferModal(payload)}
                      />
                    );
                  })
                : null}
            </Box>
          </Box>
          <Modal open={childModalFocus.modal === DashboardMainModal.DECLINE}>
            <DeclineResources
              requestResponse={childModalFocus.response}
              onOk={(confirm, payload) =>
                onDeclineResources(confirm, payload, DashboardMainModal.DECLINE)
              }
              emailMsg={emailMsg}
              changeTextEmailBody={(value) => setEmailMsg(value)}
            />
          </Modal>
        </Box>
      </Modal>

      <ConfirmationModal
        loading={sendingNotification}
        open={modalFocus.modal === DashboardMainModal.REMIND}
        title='Send reminder?'
        onConfirm={(_, ok) =>
          onConfirmSendReminder(ok, true, { requestId: modalFocus.request?.id || '', agencyId: '' })
        }>
        <React.Fragment>
          <Typo variant='body1' color='common.black' mb={3}>
            You are about to send a reminder. Do you want to continue?
          </Typo>
        </React.Fragment>
      </ConfirmationModal>

      <ConfirmationModal
        loading={processingArchive}
        open={modalFocus.modal === DashboardMainModal.ARCHIVED}
        title='Archive confirmation'
        onConfirm={(_, ok) => onConfirmArchive(ok, modalFocus.request)}>
        <React.Fragment>
          <Typo variant='body1' color='common.black' mb={2}>
            Please confirm that you want to archive this request
          </Typo>
          <Typo variant='body2' color='common.black'>
            You can review the information in the archive section
          </Typo>
        </React.Fragment>
      </ConfirmationModal>

      <Modal
        style={{ backgroundColor: '#ECEEF2' }}
        open={modalFocus.modal === DashboardMainModal.PROVIDED_RESOURCES}>
        <ProvidedResources
          responses={modalFocus.responses}
          request={modalFocus.request}
          onClose={() => onCloseProvideResources()}
        />
      </Modal>
    </>
  );
};
const RequestTotals: React.FC<{
  modalFocus: DashboardModalFocus;
  isMobile: boolean;
  classes: ClassNameMap;
}> = ({ modalFocus, isMobile, classes }) => {
  const [expanded, setExpanded] = React.useState(true);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  if (!modalFocus.request) return null;

  const ambulances = modalFocus.request.ambulance || [];
  const personnel = modalFocus.request.personnel || [];
  const ttlReqPersonnel = sumBy(personnel, (value) => Number(value.number) || 0);
  const ttlReqAmbulance = sumBy(ambulances, (value) => Number(value.number) || 0);
  const acceptedPersonnel = personnel
    .flatMap((p) => {
      const pa = [];
      pa.push(p.items?.filter((i) => i.accepted));
      return pa;
    })
    .flat();
  const ttlAcceptPersonnel = (acceptedPersonnel || []).reduce(
    (sum: number, item) => sum + (item && item.number ? item.number : 0),
    0,
  );
  const acceptedAmbulance = ambulances
    .flatMap((a) => {
      const am = [];
      am.push(a.items?.filter((i) => i.accepted));
      return am;
    })
    .flat();
  const ttlAcceptAmbulance = (acceptedAmbulance || []).reduce(
    (sum: number, item) => sum + (item && item.number ? item.number : 0),
    0,
  );

  return (
    <React.Fragment>
      <ExpandMore
        expand={expanded}
        onClick={handleExpandClick}
        aria-expanded={expanded}
        sx={{ position: 'absolute', top: '70px', right: '18px', width: '18px', height: '18px' }}
        aria-label='show more'>
        <ExpandMoreIcon sx={{ width: '18px', height: '18px' }} />
      </ExpandMore>
      <Box className={classes.contentStackView}>
        <Stack
          direction='row'
          divider={
            <Divider sx={{ marginLeft: '8px !important' }} orientation='vertical' flexItem />
          }
          spacing={2}
          sx={{ paddingBottom: expanded && isMobile ? '0' : '10px' }}>
          <Grid
            container
            columnSpacing={1}
            rowSpacing={1}
            sx={{ width: '100%', margin: 0, marginLeft: '8px !important' }}>
            <Grid xs={12} sm={12} md={4} lg={4} sx={{ height: '24px', padding: 0 }}>
              <Typo sx={{ fontSize: '16px', fontWeight: 700, color: '#0A1D40' }}>Personnel</Typo>
            </Grid>
            <Grid
              sx={{ display: expanded && isMobile ? 'none' : 'block' }}
              xs={12}
              sm={6}
              md={4}
              lg={4}
              className={classes.totalRequestGrid}>
              <Box className={classes.totalRequestBox}>
                <Typo className={classes.totalRequestLabel}>Total requested</Typo>
                <Typo
                  className={classes.totalRequestText}
                  sx={{ width: '100%', textAlign: 'right' }}>
                  {ttlReqPersonnel}
                </Typo>
              </Box>
            </Grid>
            <Grid
              sx={{ display: expanded && isMobile ? 'none' : 'block' }}
              xs={12}
              sm={6}
              md={4}
              lg={4}
              className={classes.totalAcceptGrid}>
              <Box className={classes.totalAcceptBox}>
                <Typo className={classes.totalAcceptLabel} sx={{ color: '#2961fd' }}>
                  Total accepted
                </Typo>
                <Typo
                  className={classes.totalAcceptText}
                  sx={{ width: '100%', textAlign: 'right' }}>
                  {ttlAcceptPersonnel}
                </Typo>
              </Box>
            </Grid>
          </Grid>
          <Grid container columnSpacing={1} rowSpacing={1} sx={{ width: '100%', margin: 0 }}>
            <Grid xs={12} sm={12} md={4} lg={4} sx={{ padding: 0 }}>
              <Typo sx={{ fontSize: '16px', fontWeight: 700, color: '#0A1D40' }}>Ambulances</Typo>
            </Grid>
            <Grid
              sx={{ display: expanded && isMobile ? 'none' : 'block' }}
              xs={12}
              sm={6}
              md={4}
              lg={4}
              className={classes.totalRequestAmbulanceGrid}>
              <Box className={classes.totalRequestBox}>
                <Typo className={classes.totalRequestLabel}>Total requested</Typo>
                <Typo
                  className={classes.totalRequestText}
                  sx={{ width: '100%', textAlign: 'right' }}>
                  {ttlReqAmbulance}
                </Typo>
              </Box>
            </Grid>
            <Grid
              sx={{ display: expanded && isMobile ? 'none' : 'block' }}
              xs={12}
              sm={6}
              md={4}
              lg={4}
              className={classes.totalAcceptAmbulanceGrid}>
              <Box className={classes.totalAcceptBox}>
                <Typo className={classes.totalAcceptLabel} sx={{ color: '#2961fd' }}>
                  Total accepted
                </Typo>
                <Typo
                  className={classes.totalAcceptText}
                  sx={{ width: '100%', textAlign: 'right' }}>
                  {ttlAcceptAmbulance}
                </Typo>
              </Box>
            </Grid>
          </Grid>
        </Stack>

        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <Divider sx={{ marginLeft: '-16px', marginRight: '-16px', paddingTop: '4px' }} />
          <Box sx={{ paddingTop: '10px' }}>
            <Stack
              direction='row'
              divider={
                <Divider sx={{ marginLeft: '8px !important' }} orientation='vertical' flexItem />
              }
              spacing={2}
              sx={{ paddingRight: '0', paddingBottom: '10px' }}>
              <Grid
                container
                columnSpacing={1}
                rowSpacing={1}
                sx={{
                  width: 'calc(100% - 0px)',
                  height: ambulances.length > personnel.length ? 0 : 'auto',
                  margin: 0,
                }}>
                {personnel && personnel.length ? (
                  personnel.map((person, idx: number) => {
                    const ttlRequested = person.number || 0;
                    const accepted =
                      (person && person?.items && person.items.filter((a) => a.accepted)) || [];
                    const ttlAccepted = (accepted || []).reduce(
                      (sum: number, item) => sum + (item && item.number ? item.number : 0),
                      0,
                    );

                    return (
                      <>
                        <Grid xs={6} sm={7} md={8} lg={8} sx={{ padding: '2px 0 2px 0' }}>
                          <Typo
                            className={classes.totalRequestLabel}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              paddingLeft: 0,
                              whiteSpace: 'nowrap',
                            }}>
                            {isMobile ? person.type : selectLabel(person.type, JobPositions)}
                          </Typo>
                        </Grid>
                        <Grid xs={6} sm={5} md={4} lg={4} sx={{ padding: '2px 0 2px 0' }}>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end', maxWidth: '104px' }}>
                            <Typo className={classes.totalRequestLabel}>Req:</Typo>
                            <Typo className={classes.totalRequestText}>{ttlRequested}</Typo>
                            <Typo sx={{ color: '#2D2D2F' }} className={classes.totalAcceptLabel}>
                              Acc:
                            </Typo>
                            <Typo className={classes.totalAcceptText}>{ttlAccepted}</Typo>
                          </Box>
                        </Grid>
                        <Grid xs={12} sm={12} md={12} lg={12} sx={{ padding: '6px 0 6px 0' }}>
                          <Divider sx={{ paddingTop: '0px' }} />
                        </Grid>
                      </>
                    );
                  })
                ) : (
                  <></>
                )}
              </Grid>
              <Grid
                container
                columnSpacing={1}
                rowSpacing={1}
                sx={{
                  width: 'calc(100% - 0px)',
                  height: personnel.length > ambulances.length ? 0 : 'auto',
                  marginLeft: '8px !important',
                }}>
                {ambulances && ambulances.length ? (
                  ambulances.map((ambulance, idx: number) => {
                    const ttlRequested = ambulance.number || 0;
                    const accepted =
                      (ambulance &&
                        ambulance?.items &&
                        ambulance.items.filter((a) => a.accepted)) ||
                      [];
                    const ttlAccepted = (accepted || []).reduce(
                      (sum: number, item) => sum + (item && item.number ? item.number : 0),
                      0,
                    );
                    return (
                      <>
                        <Grid xs={6} sm={7} md={8} lg={8} sx={{ padding: '2px 0 2px 0' }}>
                          <Typo
                            className={classes.totalRequestLabel}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              paddingLeft: 0,
                              whiteSpace: 'nowrap',
                            }}>
                            {isMobile
                              ? ambulance.vehicleDescription
                              : selectLabel(ambulance.vehicleDescription, AmbulanceOptions)}
                          </Typo>
                        </Grid>
                        <Grid xs={6} sm={5} md={4} lg={4} sx={{ padding: '2px 0 2px 0' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              paddingRight: '6px',
                              maxWidth: '104px',
                            }}>
                            <Typo className={classes.totalRequestLabel}>Req:</Typo>
                            <Typo className={classes.totalRequestText}>{ttlRequested}</Typo>
                            <Typo sx={{ color: '#2D2D2F' }} className={classes.totalAcceptLabel}>
                              Acc:
                            </Typo>
                            <Typo className={classes.totalAcceptText}>{ttlAccepted}</Typo>
                          </Box>
                        </Grid>
                        <Grid xs={12} sm={12} md={12} lg={12} sx={{ padding: '6px 0 6px 0' }}>
                          <Divider sx={{ paddingTop: '0px' }} />
                        </Grid>
                      </>
                    );
                  })
                ) : (
                  <></>
                )}
              </Grid>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </React.Fragment>
  );
};

export default DashboardMainView;
